// SubscriptionStatusPage — how one subscription stands, and what runs under it.
//
// The first of the three flows this phase composes rather than ports: the
// design drop has no screen for it, so the layout is assembled from
// design-system components in the drop's own idiom — the page header of its
// Fleet and Metering pages, its card-and-table pairing, its badge tones — and
// is flagged for the async design pass. Nothing here is a local style; every
// colour and spacing comes from the DS.
//
// What it shows is the JSON of subscriptions/subscription_status.html, in the
// same order: the subscription, then what is deployed under it, then what has
// been asked for, then the cancellation if there is one. The one thing the
// template does not have is the pair of actions in the header, because a
// template page reaches those by link.
import { useCallback } from 'react';
import { ArrowLeft, Server, Ban } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@miq/design-system/components/ui/table';
import { Badge } from '@miq/design-system/components/ui/badge';

import { StateBadge } from '../components/StateBadge';
import { onDay, when } from '../data/instants';
import { instanceShown } from '../data/presentation';
import { usePortalSession } from '../auth/context';
import { fetchStatus } from '../data/subscriptions';
import { useLoaded } from '../data/useLoaded';
import { useDocumentTitle } from '../../lib/documentTitle';

export interface SubscriptionStatusPageProps {
  publicId: string;
  onBack: () => void;
  onRequestDeployment: () => void;
  onEnd: () => void;
}

export function SubscriptionStatusPage({
  publicId,
  onBack,
  onRequestDeployment,
  onEnd,
}: SubscriptionStatusPageProps) {
  useDocumentTitle('Subscription');
  const { authorizedFetch } = usePortalSession();
  const read = useCallback(
    () => fetchStatus(authorizedFetch, publicId),
    [authorizedFetch, publicId],
  );
  const { state } = useLoaded(read);

  if (state.status === 'loading') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Reading this subscription…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.status === 'refused') {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" size="sm" onPress={onBack}>
          <ArrowLeft className="size-3.5" /> Subscriptions
        </Button>
        <Card>
          <CardContent className="py-8">
            {/* The backend answers a subscription belonging to somebody else
                and one belonging to nobody with the same sentence, on purpose.
                It is shown as given rather than softened into "not found", so
                the screen cannot become the oracle the API refuses to be. */}
            <p role="alert" className="text-sm text-destructive">
              {state.refusal}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { subscription, customer, instances, provisioning_requests, cancellation } =
    state.data;

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Button variant="ghost" size="sm" className="-ml-2 mb-1" onPress={onBack}>
            <ArrowLeft className="size-3.5" /> Subscriptions
          </Button>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Commerce · {customer.display_name}
          </div>
          <h1 className="mt-1 flex items-center gap-2.5 text-2xl font-semibold tracking-tight">
            {subscription.agreement_ref}
            <StateBadge subscription={subscription} />
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sold through {subscription.channel}. This page describes what we
            recorded when {subscription.channel} last told us something — it asks
            the channel nothing.
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" onPress={onRequestDeployment}>
            <Server className="size-4" /> Request a deployment
          </Button>
          {/* "How to end it", not "End subscription". This service stopped
              ending subscriptions on 2026-09-10: cancelling happens in the
              marketplace that sold it, and a button here that read like an
              action would promise something the page behind it cannot do —
              which is the exact confusion the removal exists to prevent.
              Still visible whatever the state, because somebody wanting to
              cancel needs the page that says where. */}
          <Button variant="outline" onPress={onEnd}>
            <Ban className="size-4" /> How to end it
          </Button>
        </div>
      </div>

      {cancellation && (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm">
          <div className="font-semibold">An end has been requested</div>
          <p className="mt-0.5 text-muted-foreground">
            Asked for on {when(cancellation.requested_at)}. Deployments may be
            wound down from {when(cancellation.wind_down_not_before)} — not
            before, so the last billable hour can still reach the channel.
            Billing itself ends when {subscription.channel} says it does.
          </p>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agreement</CardTitle>
            <CardDescription>What the sales channel told us</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <Row label="Channel">{subscription.channel}</Row>
              <Row label="Agreement reference">
                <code className="font-mono text-xs">{subscription.agreement_ref}</code>
              </Row>
              <Row label="Our identifier">
                <code className="font-mono text-xs">{subscription.public_id}</code>
              </Row>
              <Row label="Status">{subscription.state_label}</Row>
              <Row label="Started">{onDay(subscription.started_at)}</Row>
              <Row label="Ended">{onDay(subscription.ended_at)}</Row>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Company</CardTitle>
            <CardDescription>Who this subscription belongs to</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <Row label="Name">{customer.display_name}</Row>
              <Row label="Primary contact">
                {customer.primary_contact_email || '—'}
              </Row>
              <Row label="With us since">{onDay(customer.created_at)}</Row>
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deployments</CardTitle>
          <CardDescription>
            {instances.length} running under this subscription
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {instances.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              Nothing is deployed under this subscription yet. Ask us for one and
              our team stands it up — nothing is metered until a deployment
              reports usage.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Billing identity</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {instances.map((instance) => (
                  <TableRow key={instance.public_id}>
                    <TableCell className="font-medium">{instance.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {instance.client_id}
                    </TableCell>
                    <TableCell>
                      <Badge variant={instanceShown(instance).variant}>{instanceShown(instance).label}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {onDay(instance.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Deployment requests</CardTitle>
          <CardDescription>
            What you have asked for, and where each ask stands
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {provisioning_requests.length === 0 ? (
            <p className="px-6 py-8 text-sm text-muted-foreground">
              You have not asked us for a deployment under this subscription.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Region</TableHead>
                  {/* Read back to the person who typed it. This is the
                      only way into the deployment they are waiting for,
                      and the moment to catch a typo is while it is still
                      a request rather than after somebody has built an
                      instance around it. */}
                  <TableHead>First login</TableHead>
                  <TableHead>Decision pack</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Asked</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {provisioning_requests.map((request) => (
                  <TableRow key={request.public_id}>
                    <TableCell>
                      <div className="font-medium">{request.instance_name}</div>
                      {request.note && (
                        <div className="text-xs text-muted-foreground">
                          {request.note}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {request.region}
                    </TableCell>
                    {/* Empty on a request filed before we began asking —
                        shown as a dash rather than as the requester's
                        address, which would be us answering for them. */}
                    <TableCell className="text-muted-foreground">
                      {request.initial_user_email || '—'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {request.decision_pack}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{request.state}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {when(request.requested_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium">{children}</dd>
    </div>
  );
}
